import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

export interface CustomerAccount {
  id: string;
  organization_id: string;
  customer_id: string | null;
  username: string;
  customer_number: string;
  wallet_balance: number;
  status: 'active' | 'suspended' | 'inactive';
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined from customers table
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface WalletTransaction {
  id: string;
  customer_account_id: string;
  organization_id: string;
  transaction_type: 'top_up' | 'payment' | 'refund' | 'adjustment';
  amount: number;
  balance_before: number;
  balance_after: number;
  order_id: string | null;
  processed_by: string | null;
  notes: string | null;
  created_at: string;
  // Joined
  processed_by_name?: string | null;
}

export interface CreateCustomerAccountInput {
  username: string;
  password: string;
  pin?: string;
  customer_id?: string;  // Link to existing customer (preferred)
  initial_balance?: number;
  // Only used if customer_id is not provided - creates a new customer
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

export interface UpdateCustomerAccountInput {
  username?: string;
  status?: 'active' | 'suspended' | 'inactive';
  customer_id?: string;
}

export function useCustomerAccounts(searchQuery?: string) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch customer accounts
  const accountsQuery = useQuery({
    queryKey: ['customer-accounts', profile?.organization_id, searchQuery],
    queryFn: async () => {
      if (!profile?.organization_id) return [];

      let query = supabase
        .from('customer_accounts')
        .select(`
          *,
          customer:customers(first_name, last_name, email, phone)
        `)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (searchQuery && searchQuery.trim().length > 0) {
        const search = searchQuery.trim().toLowerCase();
        query = query.or(
          `username.ilike.%${search}%,customer_number.ilike.%${search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;

      // Flatten the customer data
      return (data || []).map((account: any) => ({
        ...account,
        first_name: account.customer?.first_name,
        last_name: account.customer?.last_name,
        email: account.customer?.email,
        phone: account.customer?.phone,
        customer: undefined,
      })) as CustomerAccount[];
    },
    enabled: !!profile?.organization_id,
    staleTime: 30000,
  });

  // Create customer account using server-side bcrypt hashing
  const createAccount = useMutation({
    mutationFn: async (input: CreateCustomerAccountInput) => {
      if (!profile?.organization_id) throw new Error('No organization');

      let customerId = input.customer_id;

      // Create customer record if needed
      if (!customerId && (input.first_name || input.last_name)) {
        const { data: newCustomer, error: customerError } = await supabase
          .from('customers')
          .insert({
            organization_id: profile.organization_id,
            first_name: input.first_name || null,
            last_name: input.last_name || null,
            email: input.email || null,
            phone: input.phone || null,
          })
          .select()
          .single();

        if (customerError) throw customerError;
        customerId = newCustomer.id;
      }

      // Use server-side RPC for bcrypt password hashing
      const { data: rpcData, error: rpcError } = await supabase.rpc('create_customer_account', {
        p_organization_id: profile.organization_id,
        p_username: input.username,
        p_password: input.password,
        p_pin: input.pin || null,
        p_customer_id: customerId || null,
        p_initial_balance: input.initial_balance || 0,
        p_created_by: profile.id,
      });

      if (rpcError) throw rpcError;

      const result = rpcData?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to create account');
      }

      // Fetch the created account with customer data
      const { data, error } = await supabase
        .from('customer_accounts')
        .select(`
          *,
          customer:customers(first_name, last_name, email, phone)
        `)
        .eq('id', result.account_id)
        .single();

      if (error) throw error;

      return {
        ...data,
        first_name: data.customer?.first_name,
        last_name: data.customer?.last_name,
        email: data.customer?.email,
        phone: data.customer?.phone,
        customer: undefined,
      } as CustomerAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
    },
  });

  // Update customer account
  const updateAccount = useMutation({
    mutationFn: async ({ id, ...updates }: UpdateCustomerAccountInput & { id: string }) => {
      const { data, error } = await supabase
        .from('customer_accounts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as CustomerAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
    },
  });

  // Reset password using server-side bcrypt hashing
  const resetPassword = useMutation({
    mutationFn: async ({ id, newPassword }: { id: string; newPassword: string }) => {
      const { data, error } = await supabase.rpc('reset_customer_password', {
        p_account_id: id,
        p_new_password: newPassword,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to reset password');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
    },
  });

  // Set/Update PIN using server-side bcrypt hashing
  const setPin = useMutation({
    mutationFn: async ({ id, pin }: { id: string; pin: string }) => {
      const { data, error } = await supabase.rpc('set_customer_pin', {
        p_account_id: id,
        p_pin: pin,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to set PIN');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
    },
  });

  // Clear PIN using server-side function
  const clearPin = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('set_customer_pin', {
        p_account_id: id,
        p_pin: null,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.error_message || 'Failed to clear PIN');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
    },
  });

  // Top up wallet
  const topUpWallet = useMutation({
    mutationFn: async ({ accountId, amount, notes }: { accountId: string; amount: number; notes?: string }) => {
      if (!profile?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('top_up_wallet', {
        p_customer_account_id: accountId,
        p_amount: amount,
        p_processed_by: profile.id,
        p_notes: notes || null,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to top up wallet');
      }

      return {
        newBalance: result.new_balance,
        transactionId: result.transaction_id,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
  });

  // Adjust wallet balance (admin)
  const adjustWallet = useMutation({
    mutationFn: async ({ accountId, amount, notes }: { accountId: string; amount: number; notes: string }) => {
      if (!profile?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase.rpc('adjust_wallet_balance', {
        p_customer_account_id: accountId,
        p_amount: amount,
        p_processed_by: profile.id,
        p_notes: notes,
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to adjust wallet');
      }

      return {
        newBalance: result.new_balance,
        transactionId: result.transaction_id,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
    },
  });

  // Delete account
  const deleteAccount = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('customer_accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
    },
  });

  return {
    accounts: accountsQuery.data || [],
    isLoading: accountsQuery.isLoading,
    error: accountsQuery.error,
    createAccount,
    updateAccount,
    resetPassword,
    setPin,
    clearPin,
    topUpWallet,
    adjustWallet,
    deleteAccount,
    refetch: accountsQuery.refetch,
  };
}

// Hook for wallet transaction history
export function useWalletTransactions(accountId: string | null) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['wallet-transactions', accountId],
    queryFn: async () => {
      if (!accountId || !profile?.organization_id) return [];

      const { data, error } = await supabase
        .from('wallet_transactions')
        .select(`
          *,
          processed_by_profile:profiles!wallet_transactions_processed_by_fkey(first_name, last_name)
        `)
        .eq('customer_account_id', accountId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      return (data || []).map((txn: any) => ({
        ...txn,
        processed_by_name: txn.processed_by_profile
          ? `${txn.processed_by_profile.first_name || ''} ${txn.processed_by_profile.last_name || ''}`.trim()
          : null,
        processed_by_profile: undefined,
      })) as WalletTransaction[];
    },
    enabled: !!accountId && !!profile?.organization_id,
  });
}

// Hook for single account details
export function useCustomerAccount(accountId: string | null) {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['customer-account', accountId],
    queryFn: async () => {
      if (!accountId || !profile?.organization_id) return null;

      const { data, error } = await supabase
        .from('customer_accounts')
        .select(`
          *,
          customer:customers(first_name, last_name, email, phone)
        `)
        .eq('id', accountId)
        .single();

      if (error) throw error;

      return {
        ...data,
        first_name: data.customer?.first_name,
        last_name: data.customer?.last_name,
        email: data.customer?.email,
        phone: data.customer?.phone,
        customer: undefined,
      } as CustomerAccount;
    },
    enabled: !!accountId && !!profile?.organization_id,
  });
}

